import { subYears } from 'date-fns';
import { transformActivityToAssessments } from 'js/routes/TeamRubric/components/RubricAssessmentDifferential/transformActivityToAssessments';
import {
  getCategoryLookups,
  getQuestionSetCategory,
} from 'js/utils/question-set';
import { titleCase } from 'js/utils/string';
import {
  filterRubricDocumentForTemplate,
  isRubricQuestionActiveAt,
  isRubricQuestionSetActiveAt,
  parseWeight,
  weightedMean,
} from 'js/utils/rubric';
import { parseDateToDate } from 'js/utils/model';
import {
  applyArrayIndexOrdinals,
  orderAssessmentQuestionSets,
  sortQuestionSets,
  sortQuestions,
} from 'js/utils/org-assessment-order';

const TREND_YEARS = 2;
const TREND_Y_TOP_PADDING = 0.2;
const TREND_X_PADDING_RATIO = 0.03;

export const TREND_Y_DOMAIN = [0, 1 + TREND_Y_TOP_PADDING];

export function formatTrendScorePercent(score) {
  if (score == null) {
    return null;
  }

  const normalized = Number(score);

  if (!Number.isFinite(normalized)) {
    return null;
  }

  return Math.round(normalized * 100);
}

export function formatTrendTooltipDate(date) {
  const parsed = date instanceof Date ? date : parseTrendDate(date);

  if (!parsed || Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

const TREND_TOOLTIP_OFFSET_X = 12;
const TREND_TOOLTIP_OFFSET_Y = 14;
export const TREND_TOOLTIP_MAX_WIDTH = 240;

export const TREND_TOOLTIP_OFFSETS = {
  left: TREND_TOOLTIP_OFFSET_X,
  top: TREND_TOOLTIP_OFFSET_Y,
};

export function getTrendTooltipTransform({
  horizontal = 'right',
  vertical = 'up',
} = {}) {
  const x =
    horizontal === 'left'
      ? `calc(-100% - ${TREND_TOOLTIP_OFFSET_X}px)`
      : `${TREND_TOOLTIP_OFFSET_X}px`;
  const y =
    vertical === 'down'
      ? `${TREND_TOOLTIP_OFFSET_Y}px`
      : `calc(-100% - ${TREND_TOOLTIP_OFFSET_Y}px)`;

  return `translate(${x}, ${y})`;
}

function trendAnchorIndex(data, anchorDatum) {
  if (!anchorDatum) {
    return -1;
  }

  const anchorTime = anchorDatum.date?.getTime?.() ?? anchorDatum.date;
  const anchorScore = Number(anchorDatum.score);

  return data.findIndex((datum) => {
    if (datum?.score == null) {
      return false;
    }

    const datumTime = datum.date?.getTime?.() ?? datum.date;

    return datumTime === anchorTime && Number(datum.score) === anchorScore;
  });
}

function resolveTrendTooltipHorizontal(
  anchorChartX,
  chartWidth,
  maxTooltipWidth = TREND_TOOLTIP_MAX_WIDTH
) {
  const rightPlacementClippedPx =
    anchorChartX + TREND_TOOLTIP_OFFSET_X + maxTooltipWidth - chartWidth;
  const leftPlacementClippedPx =
    maxTooltipWidth + TREND_TOOLTIP_OFFSET_X - anchorChartX;

  if (
    rightPlacementClippedPx > 0 &&
    rightPlacementClippedPx > leftPlacementClippedPx
  ) {
    return 'left';
  }

  return 'right';
}

function resolveTrendTooltipVertical({ horizontal, data, anchorDatum }) {
  const points = data.filter((datum) => datum?.score != null);
  const anchorIndex = trendAnchorIndex(points, anchorDatum);
  const currentScore = Number(anchorDatum.score);

  if (horizontal === 'left') {
    const previous = anchorIndex > 0 ? points[anchorIndex - 1] : null;

    if (previous != null && Number(previous.score) > currentScore) {
      return 'down';
    }

    return 'up';
  }

  const next =
    anchorIndex >= 0 && anchorIndex < points.length - 1
      ? points[anchorIndex + 1]
      : null;

  if (next != null && Number(next.score) > currentScore) {
    return 'down';
  }

  return 'up';
}

/**
 * Anchor coordinates and CSS transform for a trend line tooltip.
 * Horizontal placement uses max tooltip width; transform uses -100% so left-side
 * tooltips hug the anchor regardless of rendered label length.
 */
export function computeTrendTooltipPosition({
  anchorChartX,
  chartWidth,
  marginLeft = 0,
  marginTop = 0,
  anchorChartY = 0,
  data = [],
  anchorDatum,
}) {
  const horizontal = resolveTrendTooltipHorizontal(anchorChartX, chartWidth);
  const vertical = resolveTrendTooltipVertical({
    horizontal,
    data,
    anchorDatum,
  });
  const placement = { horizontal, vertical };

  return {
    left: marginLeft + anchorChartX,
    top: marginTop + anchorChartY,
    placement,
    transform: getTrendTooltipTransform(placement),
    vertical,
  };
}

/** Latest scored point on a trend line (default tooltip anchor when locked). */
export function getTrendLineLastAnchorDatum(data = []) {
  const points = data.filter((datum) => datum?.score != null);

  if (!points.length) {
    return null;
  }

  return points.reduce((latest, point) => {
    const pointTime = point.date?.getTime?.() ?? point.date;
    const latestTime = latest.date?.getTime?.() ?? latest.date;

    return pointTime >= latestTime ? point : latest;
  });
}

export function buildTrendLineTooltipState({
  line,
  anchorDatum,
  chartWidth,
  chartMargin,
  x,
  y,
}) {
  if (!line || !anchorDatum || !line.data?.length) {
    return null;
  }

  const anchorChartX = x(anchorDatum);
  const anchorChartY = y(anchorDatum);
  const tooltipPosition = computeTrendTooltipPosition({
    anchorChartX,
    anchorChartY,
    chartWidth,
    marginLeft: chartMargin.left,
    marginTop: chartMargin.top,
    data: line.data,
    anchorDatum,
  });

  return {
    tooltipLeft: tooltipPosition.left,
    tooltipTop: tooltipPosition.top,
    tooltipData: {
      label: line.label,
      isRollup: line.isRollup,
      depth: line.depth ?? 0,
      score: anchorDatum.score,
      date: anchorDatum.date,
      placement: tooltipPosition.placement,
      transform: tooltipPosition.transform,
    },
  };
}

/**
 * Pick the closest scored data point on a line to the pointer.
 */
export function findTrendLineTooltipAnchor({
  data = [],
  pointerX,
  pointerY,
  projectX,
  projectY,
}) {
  if (!data.length || pointerX == null || pointerY == null) {
    return null;
  }

  const projected = data
    .filter((datum) => datum?.score != null)
    .map((datum) => ({
      datum,
      px: projectX(datum),
      py: projectY(datum),
    }));

  if (!projected.length) {
    return null;
  }

  const pointTime = (datum) => datum.date?.getTime?.() ?? datum.date;

  return projected.reduce((closest, candidate) => {
    const closestDistance = Math.hypot(
      closest.px - pointerX,
      closest.py - pointerY
    );
    const candidateDistance = Math.hypot(
      candidate.px - pointerX,
      candidate.py - pointerY
    );

    if (candidateDistance !== closestDistance) {
      return candidateDistance < closestDistance ? candidate : closest;
    }

    return pointTime(candidate.datum) >= pointTime(closest.datum)
      ? candidate
      : closest;
  }).datum;
}

/** Stable string id for a team line across all org-trends charts. */
export function normalizeTeamLineId(teamId) {
  if (teamId == null || teamId === '') {
    return null;
  }

  return String(teamId);
}

function dedupeTeamsRaw(teamsRaw) {
  const byId = new Map();

  teamsRaw.forEach((team) => {
    const teamId = normalizeTeamLineId(team.teamId);

    if (teamId == null || byId.has(teamId)) {
      return;
    }

    byId.set(teamId, { ...team, teamId });
  });

  return Array.from(byId.values());
}

export function parseTrendDate(value) {
  if (value instanceof Date) {
    return value;
  }
  const parsed = parseDateToDate(value);
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
}

export function enrichTrendWindow(window) {
  if (!window?.minDate || !window?.maxDate) {
    return window;
  }

  const rangeMs = window.maxDate.getTime() - window.minDate.getTime();
  const paddingMs = Math.max(rangeMs * TREND_X_PADDING_RATIO, 0);

  return {
    ...window,
    xMaxDate: new Date(window.maxDate.getTime() + paddingMs),
  };
}

export function computeTrendWindow(allDates) {
  const dates = allDates
    .map(parseTrendDate)
    .filter((d) => d && !Number.isNaN(d.getTime()));

  if (dates.length === 0) {
    return null;
  }

  const minDataDate = new Date(Math.min(...dates.map((d) => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));
  const twoYearsPrior = subYears(maxDate, TREND_YEARS);
  const minDate = new Date(
    Math.max(twoYearsPrior.getTime(), minDataDate.getTime())
  );

  return enrichTrendWindow({ minDate, maxDate });
}

export function applyTrendWindow(points, window) {
  if (!window || !Array.isArray(points) || points.length === 0) {
    return [];
  }

  const { minDate, maxDate } = window;
  const sorted = [...points].sort((a, b) => a.date - b.date);

  const inWindow = sorted.filter((p) => p.date >= minDate && p.date <= maxDate);

  if (inWindow.length === 0) {
    return [];
  }

  const bridge = sorted.filter((p) => p.date < minDate).pop();

  return bridge ? [bridge, ...inWindow] : inWindow;
}

function scoreFromQuestion(question) {
  const response = question?.responses?.[0];
  if (!response || response.is_na) {
    return null;
  }
  const score = Number(response.value);
  return Number.isFinite(score) ? score : null;
}

/**
 * The team's effective weights, by question set key and question key. Its
 * latest evaluation from the API already has its weight scheme (named or org
 * custom) applied. Retired nodes are stripped from that evaluation, so those
 * fall back to the document's own weights.
 */
function rubricTeamWeights(evaluation, document) {
  const evalSets = new Map(
    (evaluation?.question_sets ?? []).map((qs) => [qs.key, qs])
  );

  return new Map(
    (document?.question_sets ?? []).map((qsDoc) => {
      const evalSet = evalSets.get(qsDoc.key);
      const evalQuestions = new Map(
        (evalSet?.questions ?? []).map((q) => [q.key, q])
      );
      const questionWeights = new Map(
        (qsDoc.questions ?? []).map((qDoc) => {
          const evalQuestion = evalQuestions.get(qDoc.key);
          const weight =
            evalQuestion && 'weight' in evalQuestion
              ? evalQuestion.weight
              : qDoc.weight;
          return [qDoc.key, parseWeight(weight)];
        })
      );
      const setWeight =
        evalSet && 'weight' in evalSet ? evalSet.weight : qsDoc.weight;

      return [
        qsDoc.key,
        { weight: parseWeight(setWeight), questions: questionWeights },
      ];
    })
  );
}

function buildEvaluationAtDate(assessment, document, asOf, weights) {
  const filteredDoc = filterRubricDocumentForTemplate(document, asOf);
  const activeSetKeys = new Set(
    (filteredDoc?.question_sets ?? []).map((qs) => qs.key)
  );
  const activeQuestionKeysBySet = Object.fromEntries(
    (filteredDoc?.question_sets ?? []).map((qs) => [
      qs.key,
      new Set((qs.questions ?? []).map((q) => q.key)),
    ])
  );

  const questionSets = (document?.question_sets ?? []).map((qsDoc) => {
    const qsAssessment = assessment.question_sets?.find(
      (qs) => qs.key === qsDoc.key
    );
    const setActive = activeSetKeys.has(qsDoc.key);
    const activeQuestionKeys = activeQuestionKeysBySet[qsDoc.key] ?? new Set();
    const setWeights = weights.get(qsDoc.key);
    const setWeight = setWeights?.weight ?? null;

    const questions = (qsDoc.questions ?? []).map((qDoc) => {
      const qAssessment = qsAssessment?.questions?.find(
        (q) => q.key === qDoc.key
      );
      const score = scoreFromQuestion(qAssessment);
      const weight = setWeights?.questions.get(qDoc.key) ?? null;
      const questionActive = activeQuestionKeys.has(qDoc.key);

      return {
        key: qDoc.key,
        text: qDoc.text ?? qDoc.name ?? qDoc.key,
        weight,
        score,
        included_in_numeric: questionActive && weight != null && score != null,
      };
    });

    const questionPairs = questions
      .filter((q) => q.included_in_numeric)
      .map((q) => [q.score, q.weight]);

    return {
      key: qsDoc.key,
      name: qsDoc.name ?? qsDoc.key,
      weight: setWeight,
      score: weightedMean(questionPairs),
      included_in_numeric: setActive && setWeight != null,
      questions,
    };
  });

  return {
    question_sets: questionSets,
    aggregate_score: weightedMean(
      questionSets
        .filter((qs) => qs.included_in_numeric && qs.score != null)
        .map((qs) => [qs.score, qs.weight])
    ),
  };
}

function activeAssessmentInstances(instances) {
  return (instances ?? []).filter((instance) => instance.active !== false);
}

function findQuestionSetDoc(document, qsKey) {
  return (document?.question_sets ?? []).find((qs) => qs.key === qsKey);
}

function findQuestionDoc(qsDoc, questionKey) {
  return (qsDoc?.questions ?? []).find(
    (q) =>
      q.key === questionKey ||
      (q.guid != null && String(q.guid) === String(questionKey))
  );
}

function canIncludeQuestionSetTrendPoint(document, qsKey, asOf) {
  const qsDoc = findQuestionSetDoc(document, qsKey);
  return qsDoc != null && isRubricQuestionSetActiveAt(qsDoc, asOf);
}

function canIncludeQuestionTrendPoint(document, qsKey, questionKey, asOf) {
  const qsDoc = findQuestionSetDoc(document, qsKey);
  if (!qsDoc || !isRubricQuestionSetActiveAt(qsDoc, asOf)) {
    return false;
  }

  const qDoc = findQuestionDoc(qsDoc, questionKey);
  return qDoc != null && isRubricQuestionActiveAt(qsDoc, qDoc, asOf);
}

function canIncludeOverallTrendPoint(document, evaluation, asOf) {
  return (evaluation.question_sets ?? []).some(
    (qs) =>
      qs.included_in_numeric &&
      qs.score != null &&
      canIncludeQuestionSetTrendPoint(document, qs.key, asOf)
  );
}

function buildRubricTeamSeries(team, document) {
  const instances = activeAssessmentInstances(team.assessment_instances);
  const questionSets = document?.question_sets ?? [];
  const weights = rubricTeamWeights(team.evaluation, document);
  const assessments = transformActivityToAssessments(
    questionSets,
    instances,
    team.assessment_score_maps ?? {},
    true
  );

  const overall = [];
  const questionSetsSeries = {};
  const questionsSeries = {};

  assessments.forEach((assessment) => {
    const asOf = assessment.inserted_at;
    const evaluation = buildEvaluationAtDate(
      assessment,
      document,
      asOf,
      weights
    );

    if (
      evaluation.aggregate_score != null &&
      canIncludeOverallTrendPoint(document, evaluation, asOf)
    ) {
      overall.push({ date: asOf, score: evaluation.aggregate_score });
    }

    evaluation.question_sets.forEach((qs) => {
      if (
        qs.included_in_numeric &&
        qs.score != null &&
        canIncludeQuestionSetTrendPoint(document, qs.key, asOf)
      ) {
        if (!questionSetsSeries[qs.key]) {
          questionSetsSeries[qs.key] = [];
        }
        questionSetsSeries[qs.key].push({ date: asOf, score: qs.score });
      }

      (qs.questions ?? []).forEach((q) => {
        if (
          !q.included_in_numeric ||
          q.score == null ||
          !canIncludeQuestionTrendPoint(document, qs.key, q.key, asOf)
        ) {
          return;
        }
        const cellKey = `${qs.key}|${q.key}`;
        if (!questionsSeries[cellKey]) {
          questionsSeries[cellKey] = [];
        }
        questionsSeries[cellKey].push({ date: asOf, score: q.score });
      });
    });
  });

  return {
    teamId: normalizeTeamLineId(team.team_id),
    teamName: team.team_name,
    rawSeries: {
      overall,
      questionSets: questionSetsSeries,
      questions: questionsSeries,
    },
  };
}

function normalizeNativeTeam(team) {
  const mapPoints = (points = []) =>
    points.map((p) => ({
      date: parseTrendDate(p.date),
      score: p.score,
    }));

  return {
    teamId: normalizeTeamLineId(team.team_id),
    teamName: team.team_name,
    rawSeries: {
      overall: mapPoints(team.series?.overall),
      questionSets: Object.fromEntries(
        Object.entries(team.series?.question_sets ?? {}).map(
          ([key, points]) => [key, mapPoints(points)]
        )
      ),
      questions: Object.fromEntries(
        Object.entries(team.series?.questions ?? {}).map(([key, points]) => [
          key,
          mapPoints(points),
        ])
      ),
    },
  };
}

function collectAllDates(teamsRaw) {
  const dates = [];
  teamsRaw.forEach(({ rawSeries }) => {
    rawSeries.overall.forEach((p) => dates.push(p.date));
    Object.values(rawSeries.questionSets).forEach((series) =>
      series.forEach((p) => dates.push(p.date))
    );
    Object.values(rawSeries.questions).forEach((series) =>
      series.forEach((p) => dates.push(p.date))
    );
  });
  return dates;
}

function applyWindowToTeamSeries(rawSeries, window) {
  return {
    overall: applyTrendWindow(rawSeries.overall, window),
    questionSets: Object.fromEntries(
      Object.entries(rawSeries.questionSets).map(([key, points]) => [
        key,
        applyTrendWindow(points, window),
      ])
    ),
    questions: Object.fromEntries(
      Object.entries(rawSeries.questions).map(([key, points]) => [
        key,
        applyTrendWindow(points, window),
      ])
    ),
  };
}

function hasVisibleWindowPoints(series, window) {
  if (!window || !series?.length) {
    return false;
  }
  return series.some(
    (p) => p.date >= window.minDate && p.date <= window.maxDate
  );
}

function rubricQuestionSetMeta(document) {
  const asOf = new Date();

  const questionSets = applyArrayIndexOrdinals(document?.question_sets ?? []);

  return orderAssessmentQuestionSets(questionSets).map((qs) => ({
    key: qs.key,
    name: qs.name ?? qs.key,
    category: getQuestionSetCategory(qs),
    ordinal: qs.ordinal ?? null,
    retired: !isRubricQuestionSetActiveAt(qs, asOf),
    questions: (qs.questions ?? []).map((q) => ({
      key: q.key,
      text: q.text ?? q.name ?? q.key,
      ordinal: q.ordinal ?? null,
      retired: !isRubricQuestionActiveAt(qs, q, asOf),
    })),
  }));
}

function nativeQuestionSetMeta(questionSets = [], assessmentTemplate = null) {
  return orderAssessmentQuestionSets(questionSets, assessmentTemplate).map(
    (qs) => ({
      key: qs.key,
      name: qs.name ?? qs.key,
      category: getQuestionSetCategory(qs),
      ordinal: qs.ordinal ?? null,
      inserted_at: qs.inserted_at ?? null,
      retired: false,
      questions: (qs.questions ?? []).map((q) => ({
        key: q.key,
        text: q.text ?? q.key,
        ordinal: q.ordinal ?? null,
        inserted_at: q.inserted_at ?? null,
        retired: false,
      })),
    })
  );
}

function buildRubricCategoryLookups(questionSets) {
  const keys = [
    ...new Set(questionSets.map((qs) => getQuestionSetCategory(qs))),
  ].sort();
  const map = new Map();
  let ci = 0;

  keys.forEach((key) => {
    map.set(key, {
      display_name:
        key === 'uncategorized'
          ? 'Uncategorized'
          : titleCase(String(key).replace(/-/g, ' ')),
      palette: `palette-cat-${++ci}`,
    });
  });

  return map;
}

export function buildOrgTrendsCategoryLookups({
  isRubric = false,
  assessmentTemplate = null,
  questionSets = [],
} = {}) {
  if (isRubric) {
    return buildRubricCategoryLookups(questionSets);
  }

  const supplementalCategories = {};

  questionSets.forEach((qs) => {
    const category = getQuestionSetCategory(qs);
    supplementalCategories[category] = {
      ...supplementalCategories[category],
      [qs.key]: null,
    };
  });

  return getCategoryLookups(
    [
      {
        config:
          assessmentTemplate?.config ?? assessmentTemplate?.assessment?.config,
      },
    ],
    supplementalCategories
  );
}

function filterQuestionSetsWithData(questionSets, teams, window) {
  return questionSets
    .map((qs) => {
      const questions = sortQuestions(
        qs.questions.filter((q) => {
          const cellKey = `${qs.key}|${q.key}`;
          return teams.some((team) =>
            hasVisibleWindowPoints(team.series.questions[cellKey], window)
          );
        })
      );

      const setHasData = teams.some((team) =>
        hasVisibleWindowPoints(team.series.questionSets[qs.key], window)
      );

      if (!setHasData && questions.length === 0) {
        return null;
      }

      return { ...qs, questions };
    })
    .filter(Boolean);
}

function finalizeTrendData({ questionSets, teamsRaw }) {
  const uniqueTeamsRaw = dedupeTeamsRaw(teamsRaw);
  const window = computeTrendWindow(collectAllDates(uniqueTeamsRaw));

  const teams = uniqueTeamsRaw.map(({ teamId, teamName, rawSeries }) => ({
    teamId,
    teamName,
    series: window ? applyWindowToTeamSeries(rawSeries, window) : rawSeries,
  }));

  const filteredQuestionSets = window
    ? sortQuestionSets(filterQuestionSetsWithData(questionSets, teams, window))
    : [];

  return {
    window,
    questionSets: filteredQuestionSets,
    teams,
  };
}

export function buildOrgTrendsFromRubric({ teams = [], document } = {}) {
  const questionSets = rubricQuestionSetMeta(document);
  const teamsRaw = dedupeTeamsRaw(
    teams.map((team) => buildRubricTeamSeries(team, document))
  );
  return finalizeTrendData({ questionSets, teamsRaw });
}

function appendSnapshotEvaluationPoints(entry, evaluation, document, asOf) {
  if (
    evaluation?.aggregate_score != null &&
    canIncludeOverallTrendPoint(document, evaluation, asOf)
  ) {
    entry.overall.push({ date: asOf, score: evaluation.aggregate_score });
  }

  (evaluation?.question_sets ?? []).forEach((qs) => {
    if (
      qs.included_in_numeric &&
      qs.score != null &&
      canIncludeQuestionSetTrendPoint(document, qs.key, asOf)
    ) {
      if (!entry.questionSets[qs.key]) {
        entry.questionSets[qs.key] = [];
      }
      entry.questionSets[qs.key].push({ date: asOf, score: qs.score });
    }

    (qs.questions ?? []).forEach((q) => {
      if (
        !q.included_in_numeric ||
        q.score == null ||
        !canIncludeQuestionTrendPoint(document, qs.key, q.key, asOf)
      ) {
        return;
      }
      const cellKey = `${qs.key}|${q.key}`;
      if (!entry.questions[cellKey]) {
        entry.questions[cellKey] = [];
      }
      entry.questions[cellKey].push({ date: asOf, score: q.score });
    });
  });
}

function sortSeriesByDate(points = []) {
  return [...points].sort((a, b) => a.date - b.date);
}

export function buildOrgTrendsFromSnapshots({ snapshots = [], document } = {}) {
  const questionSets = rubricQuestionSetMeta(document);
  const byTeam = new Map();

  [...snapshots]
    .sort((a, b) => {
      const aTime = new Date(a.snapshot_at || 0).getTime();
      const bTime = new Date(b.snapshot_at || 0).getTime();
      return aTime - bTime;
    })
    .forEach((snapshot) => {
      const asOf = parseTrendDate(snapshot.snapshot_at);
      if (!asOf) {
        return;
      }

      (snapshot.teams ?? []).forEach((team) => {
        const teamId = normalizeTeamLineId(team.team_id);
        if (teamId == null) {
          return;
        }

        let entry = byTeam.get(teamId);
        if (!entry) {
          entry = {
            teamId,
            teamName: team.team_name,
            overall: [],
            questionSets: {},
            questions: {},
          };
          byTeam.set(teamId, entry);
        }

        appendSnapshotEvaluationPoints(
          entry,
          team.evaluation ?? {},
          document,
          asOf
        );
      });
    });

  const teamsRaw = dedupeTeamsRaw(
    Array.from(byTeam.values()).map((entry) => ({
      teamId: entry.teamId,
      teamName: entry.teamName,
      rawSeries: {
        overall: sortSeriesByDate(entry.overall),
        questionSets: Object.fromEntries(
          Object.entries(entry.questionSets).map(([key, points]) => [
            key,
            sortSeriesByDate(points),
          ])
        ),
        questions: Object.fromEntries(
          Object.entries(entry.questions).map(([key, points]) => [
            key,
            sortSeriesByDate(points),
          ])
        ),
      },
    }))
  );

  const dates = collectAllDates(teamsRaw);
  if (!dates.length) {
    return { window: null, questionSets: [], teams: [] };
  }

  const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
  const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));
  const window = enrichTrendWindow({ minDate, maxDate });

  const teams = teamsRaw.map(({ teamId, teamName, rawSeries }) => ({
    teamId,
    teamName,
    series: applyWindowToTeamSeries(rawSeries, window),
  }));

  return {
    window,
    questionSets: sortQuestionSets(
      filterQuestionSetsWithData(questionSets, teams, window)
    ),
    teams,
  };
}

export function buildOrgTrendsFromNative(apiData, assessmentTemplate = null) {
  if (!apiData) {
    return { window: null, questionSets: [], teams: [] };
  }

  const questionSets = nativeQuestionSetMeta(
    apiData.question_sets,
    assessmentTemplate
  );
  const teamsRaw = dedupeTeamsRaw(
    (apiData.teams ?? []).map(normalizeNativeTeam)
  );

  if (apiData.window?.min_date && apiData.window?.max_date) {
    const window = enrichTrendWindow({
      minDate: parseTrendDate(apiData.window.min_date),
      maxDate: parseTrendDate(apiData.window.max_date),
    });

    const teams = teamsRaw.map(({ teamId, teamName, rawSeries }) => ({
      teamId,
      teamName,
      series: applyWindowToTeamSeries(rawSeries, window),
    }));

    return {
      window,
      questionSets: sortQuestionSets(
        filterQuestionSetsWithData(questionSets, teams, window)
      ),
      teams,
    };
  }

  return finalizeTrendData({ questionSets, teamsRaw });
}

export function getTrendSeriesForChart(teams, accessor) {
  const seenTeamIds = new Set();

  return teams
    .map((team) => {
      const id = normalizeTeamLineId(team.teamId);

      if (id == null || seenTeamIds.has(id)) {
        return null;
      }

      const data = accessor(team.series);

      if (!data?.length) {
        return null;
      }

      seenTeamIds.add(id);

      return {
        id,
        label: team.teamName,
        data,
      };
    })
    .filter(Boolean);
}
